import { IPE_WORKFLOW_STEP_CATALOG } from './ipeWorkflowSteps';
import { resolveWorkflowNavId } from './workflowNavId';
import { officialDataAPI, workflowTemplateAPI } from '@/utils/api';
import { applyWorkflowWorkspaceFields } from '@/pages/ipe/workbench/utils/workflowWorkspaceMeta';
import { canvasToStepStages, flattenStepStages, resolveCanvasFromTemplate } from '@/pages/ipe/workbench/utils/workflowCanvasModel';
import { GOODS_STUDIO_DEFAULT_PARAMS, AI_TEXT2IMG_AR_OPTIONS, AI_TEXT2IMG_STYLE_OPTIONS } from './aiStudioConfig';
import {
  DEFAULT_FISSION_ASPECT_SIZE,
  DEFAULT_OUTPAINT_EXPAND_PARAMS,
  getOutpaintPresetDimensions,
  getPresetDimensions,
} from './imageFissionAspectSize';
import {
  DEFAULT_MODEL_TRY_ON_ETHNICITY,
  DEFAULT_MODEL_TRY_ON_GARMENT_TYPE,
  DEFAULT_MODEL_TRY_ON_MODEL_TYPE,
  resolveTryOnFrameScopeForGarment,
  sanitizeModelTryOnParams,
} from './modelStudioConfig';
import { DEFAULT_AI_COPYWRITING_LANGUAGE, AI_COPYWRITING_MAX_CHARS_LIMIT } from './aiCopywritingConfig';
import { normalizeTitlePresetScope } from './titlePresetCatalog';
import {
  VIDEO_DEFAULT_DURATION_SEC,
  VIDEO_DEFAULT_GENERATION_MODE,
  VIDEO_DEFAULT_MODEL_PROMPT_TYPE,
  VIDEO_DEFAULT_SEED,
  normalizeVideoAspectRatio,
  normalizeVideoDurationSec,
  normalizeVideoGenerationMode,
  normalizeVideoModelPromptType,
  normalizeVideoSeed,
} from './videoStudioConfig';
import { getIpeUserLocalOwnerId, userLocal } from '@/utils/ipeUserLocalState';
import {
  STEP_FAILURE_POLICY_ABORT,
  normalizeStepFailurePolicy,
} from './workflowStepFailurePolicy';
import {
  isLegacyGoodsStudioLabel,
  rewriteLegacyGoodsStudioUserCopy,
} from '@/pages/ipe/workbench/utils/workflowLegacyCopy';

export const WORKFLOW_TEMPLATES_STORAGE_KEY = 'ipe-workflow-templates-v1';
export const WORKFLOW_CUSTOM_GROUPS_KEY = 'ipe-workflow-custom-groups-v1';
/** 已删除模板 id。刷新后服务端列表或本地迁移不得把它们再写回来。 */
const WORKFLOW_TEMPLATES_DELETED_IDS_KEY = 'ipe-workflow-templates-deleted-ids-v1';

let serverOfficialWorkflowCache = [];
/** 当前工作区下的自定义模板（含队友创建）；由 refreshServerCustomWorkflowTemplates 填充 */
let serverCustomWorkflowCache = [];

export async function refreshServerOfficialWorkflowTemplates() {
  try {
    const res = await officialDataAPI.listWorkflowTemplates();
    serverOfficialWorkflowCache = (res?.templates || [])
      .map((t) => normalizeTemplate({ ...t, official: true }))
      .filter(Boolean);
  } catch {
    serverOfficialWorkflowCache = [];
  }
  return serverOfficialWorkflowCache;
}

export function getCachedServerOfficialWorkflowTemplates() {
  return serverOfficialWorkflowCache;
}

export async function refreshServerCustomWorkflowTemplates() {
  try {
    const res = await workflowTemplateAPI.list();
    const deleted = loadDeletedWorkflowTemplateIds();
    serverCustomWorkflowCache = (res?.templates || res?.data || [])
      .map((t) => normalizeTemplate({ ...t, official: false }))
      .filter((t) => t && t.id && !deleted.has(t.id));
  } catch {
    serverCustomWorkflowCache = [];
  }
  return serverCustomWorkflowCache;
}

export function getCachedServerCustomWorkflowTemplates() {
  return serverCustomWorkflowCache;
}

/** 官方 + 当前工作区自定义模板一并刷新 */
export async function refreshAllServerWorkflowTemplates() {
  await Promise.all([
    refreshServerOfficialWorkflowTemplates(),
    refreshServerCustomWorkflowTemplates(),
  ]);
  return {
    official: serverOfficialWorkflowCache,
    custom: serverCustomWorkflowCache,
  };
}

function mergeLocalAndServerTemplates(localList, userId) {
  const serverOfficial = getCachedServerOfficialWorkflowTemplates();
  const serverCustom = getCachedServerCustomWorkflowTemplates();
  const deleted = loadDeletedWorkflowTemplateIds();
  const byId = new Map();

  (localList || []).forEach((t) => {
    if (!t?.id || deleted.has(t.id)) return;
    // 官方以服务端为准；本地自定义仅本人可见
    if (t.official && serverOfficial.some((s) => s.id === t.id)) return;
    if (!t.official && !isWorkflowTemplateOwnedByUser(t, userId)) return;
    byId.set(t.id, t);
  });

  serverOfficial.forEach((t) => {
    if (t?.id) byId.set(t.id, { ...t, official: true });
  });

  // 服务端自定义：后端已按工作区隔离，团队模式下含队友模板
  serverCustom.forEach((t) => {
    if (t?.id && !deleted.has(t.id)) byId.set(t.id, { ...t, official: false });
  });

  return [...byId.values()];
}

export function defaultParamsForNav(navId) {
  const base = {};
  switch (navId) {
    case 'imageCrop':
      return { ...base };
    case 'smartCutout':
      return { ...base, cutoutMode: 'basic' };
    case 'imageEnhance':
      return { ...base };
    case 'stampExtract':
      return { ...base };
    case 'goods':
      return { ...base, ...GOODS_STUDIO_DEFAULT_PARAMS, composeMode: 'ai' };
    case 'aiOutpaint':
      return { ...base, ...DEFAULT_OUTPAINT_EXPAND_PARAMS, generateCount: 1 };
    case 'aiTextToImage':
      return {
        ...base,
        prompt: '',
        text2imgPromptCaseId: '',
        text2imgPromptCaseScope: '',
        style: 'common',
        aspectRatio: '1:1',
        generateCount: 1,
      };
    case 'aiCopywriting':
      return {
        ...base,
        uploadMode: 'default',
        prompt: '',
        copywritingLanguage: DEFAULT_AI_COPYWRITING_LANGUAGE,
        titlePresetId: '',
        copywritingTitlePresetScope: '',
      };
    case 'modelTryOn':
      return {
        ...base,
        garmentType: DEFAULT_MODEL_TRY_ON_GARMENT_TYPE,
        frameScope: resolveTryOnFrameScopeForGarment(DEFAULT_MODEL_TRY_ON_GARMENT_TYPE),
        modelType: DEFAULT_MODEL_TRY_ON_MODEL_TYPE,
        selectedPresetId: '',
        selectedPresetUrl: '',
        prompt: '',
        generateCount: 1,
      };
    case 'modelHandheld':
    case 'modelUniversalWear':
      return {
        ...base,
        modelType: DEFAULT_MODEL_TRY_ON_MODEL_TYPE,
        modelEthnicity: DEFAULT_MODEL_TRY_ON_ETHNICITY,
        selectedPresetId: '',
        selectedPresetUrl: '',
        prompt: '',
        generateCount: 1,
      };
    case 'modelPoseFission':
      return { ...base, prompt: '', generateCount: 1 };
    case 'videos':
      return {
        ...base,
        supplementPrompt: '',
        generationMode: VIDEO_DEFAULT_GENERATION_MODE,
        aspectRatio: '9:16',
        durationSec: VIDEO_DEFAULT_DURATION_SEC,
        seed: VIDEO_DEFAULT_SEED,
        modelImageUrl: '',
        modelImagePreviewUrl: '',
      };
    case 'textToVideo':
      return {
        ...base,
        scriptPrompt: '',
        generationMode: VIDEO_DEFAULT_GENERATION_MODE,
        aspectRatio: '9:16',
        durationSec: VIDEO_DEFAULT_DURATION_SEC,
        seed: VIDEO_DEFAULT_SEED,
        modelPromptType: VIDEO_DEFAULT_MODEL_PROMPT_TYPE,
      };
    case 'podProductDesign':
      return { ...base, imageTemplateIds: [] };
    case 'titles':
      return {
        ...base,
        titlePresetId: '',
        titlePrompt: '',
        targetLanguage: '',
        autoOpenDetail: false,
      };
    case 'infringementDetection':
      return { ...base, filterMode: 'basic' };
    case 'platformListing':
    case 'platformListingJobs':
      return { ...base, autoCreateListingJob: true, listingTemplateIds: [] };
    case 'sourcingHub':
      return { ...base };
    case 'imageFission':
      return {
        ...base,
        aspectRatio: DEFAULT_FISSION_ASPECT_SIZE.aspectRatio,
        outputWidth: DEFAULT_FISSION_ASPECT_SIZE.outputWidth,
        outputHeight: DEFAULT_FISSION_ASPECT_SIZE.outputHeight,
        patternOnly: false,
        variationCount: 1,
      };
    case 'img2img':
      return {
        ...base,
        prompt: '',
        img2imgPromptCaseId: '',
        img2imgPromptCaseScope: '',
        generateCount: 1,
      };
    default:
      return { ...base };
  }
}

/** 套图模板 id：兼容数组、逗号串、以及 {id} 对象。 */
export function coerceImageTemplateIds(value) {
  const push = (item, out) => {
    if (item == null) return;
    if (typeof item === 'object') {
      const id = String(item.id || item.templateId || '').trim();
      if (id) out.push(id);
      return;
    }
    String(item)
      .split(/[,，]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((id) => out.push(id));
  };
  const out = [];
  if (Array.isArray(value)) value.forEach((item) => push(item, out));
  else push(value, out);
  return [...new Set(out)];
}

function loadDeletedWorkflowTemplateIds() {
  if (!getIpeUserLocalOwnerId()) return new Set();
  try {
    const parsed = userLocal.getJson(WORKFLOW_TEMPLATES_DELETED_IDS_KEY, []);
    return new Set((Array.isArray(parsed) ? parsed : []).map((id) => String(id || '').trim()).filter(Boolean));
  } catch {
    return new Set();
  }
}

function persistDeletedWorkflowTemplateIds(ids) {
  if (!getIpeUserLocalOwnerId()) return;
  userLocal.setJson(WORKFLOW_TEMPLATES_DELETED_IDS_KEY, [...ids]);
}

export function isWorkflowTemplateDeleted(templateId) {
  const id = String(templateId || '').trim();
  if (!id) return false;
  return loadDeletedWorkflowTemplateIds().has(id);
}

function rememberDeletedWorkflowTemplate(templateId) {
  const id = String(templateId || '').trim();
  if (!id) return;
  const ids = loadDeletedWorkflowTemplateIds();
  ids.add(id);
  persistDeletedWorkflowTemplateIds(ids);
  serverCustomWorkflowCache = serverCustomWorkflowCache.filter((t) => t.id !== id);
}

export function normalizeStep(s) {
  if (!s || typeof s !== 'object') {
    return { navId: '', label: '', onFailure: STEP_FAILURE_POLICY_ABORT, params: {} };
  }
  // 失败策略：每个节点独立配置，默认「终止整批」
  const onFailure = normalizeStepFailurePolicy(s.onFailure);
  const navId = resolveWorkflowNavId(s);
  const meta = IPE_WORKFLOW_STEP_CATALOG.find((x) => x.navId === navId);
  const raw = s.params && typeof s.params === 'object' && !Array.isArray(s.params) ? { ...s.params } : {};
  // 「定时上架」与「批量上架」共用 platformListing 算子，靠该标记区分节点语义
  const originNavId = String(s?.navId || '').trim();
  const originLabel = String(s?.label || '').trim();
  const scheduledListingNode = originNavId === 'platformScheduledListings'
    || originLabel === '定时上架';
  if (navId === 'imageEnhance') {
    // 清晰化参数由后端固定；清理历史倍数/质量档字段
    delete raw.enhanceType;
    delete raw.enhanceMultiple;
    delete raw.enhanceQuality;
    delete raw.denoise;
    delete raw.type;
    delete raw.enhanceDenoiseAuto;
    delete raw.scale;
  }
  if (navId === 'imageCrop') {
    delete raw.mode;
  }
  if (navId === 'aiOutpaint') {
    raw.expandMode = Number(raw.expandMode) === 2 ? 2 : 1;
    raw.generateCount = 1;
    if (raw.expandMode === 1) {
      const scale = Number(raw.scale);
      raw.scale = scale > 1 ? scale : DEFAULT_OUTPAINT_EXPAND_PARAMS.scale;
    } else {
      const ratio = String(raw.aspectRatio || DEFAULT_OUTPAINT_EXPAND_PARAMS.aspectRatio);
      raw.aspectRatio = ratio === 'custom' ? 'custom' : ratio;
      let w = Math.max(1, Math.floor(Number(raw.outputWidth) || 0));
      let h = Math.max(1, Math.floor(Number(raw.outputHeight) || 0));
      if (ratio !== 'custom') {
        const preset = getOutpaintPresetDimensions(ratio);
        if (preset) {
          w = preset.width;
          h = preset.height;
        }
      }
      if (w <= 0 || h <= 0) {
        w = DEFAULT_OUTPAINT_EXPAND_PARAMS.outputWidth;
        h = DEFAULT_OUTPAINT_EXPAND_PARAMS.outputHeight;
      }
      raw.outputWidth = w;
      raw.outputHeight = h;
    }
  }
  if (navId === 'img2img') {
    // 一图一生：忽略旧模板中可能残留的 generateCount>1
    raw.generateCount = 1;
    const caseId = raw.img2imgPromptCaseId != null ? String(raw.img2imgPromptCaseId).trim() : '';
    if (caseId) raw.img2imgPromptCaseId = caseId;
    else delete raw.img2imgPromptCaseId;
    if (raw.img2imgPromptCaseScope != null && String(raw.img2imgPromptCaseScope).trim()) {
      raw.img2imgPromptCaseScope = normalizeTitlePresetScope(raw.img2imgPromptCaseScope);
    } else {
      delete raw.img2imgPromptCaseScope;
    }
  }
  if (navId === 'aiTextToImage') {
    raw.generateCount = Math.max(1, Math.min(4, Number(raw.generateCount) || 1));
    const style = String(raw.style || 'common').trim();
    raw.style = AI_TEXT2IMG_STYLE_OPTIONS.some((o) => o.value === style) ? style : 'common';
    const ar = String(raw.aspectRatio || '1:1').trim();
    raw.aspectRatio = AI_TEXT2IMG_AR_OPTIONS.includes(ar) ? ar : '1:1';
    const caseId = raw.text2imgPromptCaseId != null ? String(raw.text2imgPromptCaseId).trim() : '';
    if (caseId) raw.text2imgPromptCaseId = caseId;
    else delete raw.text2imgPromptCaseId;
    if (raw.text2imgPromptCaseScope != null && String(raw.text2imgPromptCaseScope).trim()) {
      raw.text2imgPromptCaseScope = normalizeTitlePresetScope(raw.text2imgPromptCaseScope);
    } else {
      delete raw.text2imgPromptCaseScope;
    }
  }
  if (navId === 'aiCopywriting') {
    // 旧模板若只有标题提取字段：迁到图生文案 prompt
    if (!String(raw.prompt || '').trim() && String(raw.titlePrompt || '').trim()) {
      raw.prompt = String(raw.titlePrompt || '');
    }
    delete raw.copywritingTab;
    delete raw.titlePrompt;
    delete raw.titleTargetLanguage;
    delete raw.generateCount;
    raw.uploadMode = String(raw.uploadMode || '').trim().toLowerCase() === 'custom' ? 'custom' : 'default';
    if (!raw.copywritingLanguage) {
      raw.copywritingLanguage = DEFAULT_AI_COPYWRITING_LANGUAGE;
    }
    const presetId = raw.titlePresetId != null ? String(raw.titlePresetId).trim() : '';
    if (presetId) raw.titlePresetId = presetId;
    else delete raw.titlePresetId;
    if (raw.copywritingTitlePresetScope != null && String(raw.copywritingTitlePresetScope).trim()) {
      raw.copywritingTitlePresetScope = normalizeTitlePresetScope(raw.copywritingTitlePresetScope);
    } else {
      delete raw.copywritingTitlePresetScope;
    }
    // 字数限制：仅保留 1~500 的整数，空值表示不限制
    const maxCharsNum = Number(String(raw.maxChars ?? '').replace(/\D/g, ''));
    if (Number.isFinite(maxCharsNum) && maxCharsNum > 0) {
      raw.maxChars = Math.max(1, Math.min(AI_COPYWRITING_MAX_CHARS_LIMIT, Math.floor(maxCharsNum)));
    } else {
      delete raw.maxChars;
    }
  }
  if (navId === 'modelTryOn' || navId === 'modelHandheld' || navId === 'modelPoseFission' || navId === 'modelUniversalWear') {
    raw.generateCount = Math.max(1, Math.min(4, Number(raw.generateCount) || 1));
  }
  if (navId === 'modelTryOn') {
    const sanitized = sanitizeModelTryOnParams(raw);
    raw.modelType = sanitized.modelType;
    raw.garmentType = sanitized.garmentType;
    raw.frameScope = sanitized.frameScope;
    raw.selectedPresetId = String(raw.selectedPresetId || '').trim();
    raw.selectedPresetUrl = String(raw.selectedPresetUrl || '').trim();
    delete raw.usedUploadedModel;
    delete raw.modelImageUrl;
    delete raw.modelImagePreviewUrl;
  }
  if (navId === 'modelHandheld' || navId === 'modelUniversalWear') {
    raw.modelType = String(raw.modelType || DEFAULT_MODEL_TRY_ON_MODEL_TYPE).trim() || DEFAULT_MODEL_TRY_ON_MODEL_TYPE;
    raw.modelEthnicity = String(raw.modelEthnicity || DEFAULT_MODEL_TRY_ON_ETHNICITY).trim() || DEFAULT_MODEL_TRY_ON_ETHNICITY;
    raw.selectedPresetId = String(raw.selectedPresetId || '').trim();
    raw.selectedPresetUrl = String(raw.selectedPresetUrl || '').trim();
    delete raw.usedUploadedModel;
    delete raw.modelImageUrl;
    delete raw.modelImagePreviewUrl;
    delete raw.garmentType;
    delete raw.frameScope;
  }
  if (navId === 'modelPoseFission') {
    delete raw.modelType;
    delete raw.modelEthnicity;
    delete raw.garmentType;
    delete raw.frameScope;
    delete raw.selectedPresetId;
    delete raw.selectedPresetUrl;
    delete raw.usedUploadedModel;
    delete raw.modelImageUrl;
    delete raw.modelImagePreviewUrl;
  }
  if (navId === 'videos' || navId === 'textToVideo') {
    raw.durationSec = normalizeVideoDurationSec(raw.durationSec);
    raw.generationMode = normalizeVideoGenerationMode(raw.generationMode);
    raw.aspectRatio = normalizeVideoAspectRatio(raw.aspectRatio);
    raw.seed = normalizeVideoSeed(raw.seed);
    if (navId === 'textToVideo') {
      raw.scriptPrompt = String(raw.scriptPrompt || raw.prompt || '').trim().slice(0, 2500);
      raw.modelPromptType = normalizeVideoModelPromptType(raw.modelPromptType);
      delete raw.prompt;
      delete raw.supplementPrompt;
      delete raw.modelImageUrl;
      delete raw.modelImagePreviewUrl;
    } else {
      raw.supplementPrompt = String(raw.supplementPrompt || '').trim().slice(0, 2500);
      raw.modelImageUrl = String(raw.modelImageUrl || '').trim();
      raw.modelImagePreviewUrl = String(raw.modelImagePreviewUrl || raw.modelImageUrl || '').trim();
      delete raw.modelPromptType;
    }
    delete raw.mediaType;
    delete raw.quantity;
    delete raw.conceptMode;
    delete raw.conceptPresetId;
    delete raw.customPrompt;
  }
  if (navId === 'podProductDesign') {
    raw.imageTemplateIds = coerceImageTemplateIds(raw.imageTemplateIds);
    delete raw.stampAssignMode;
    delete raw.requireManualStampAssign;
  }
  if (navId === 'smartCutout') {
    raw.cutoutMode = String(raw.cutoutMode || raw.mode || '').trim().toLowerCase() === 'advanced'
      ? 'advanced'
      : 'basic';
    delete raw.cutoutEngine;
    delete raw.cutoutFormats;
    delete raw.cutoutFormat;
    delete raw.cutoutType;
    delete raw.cutoutEnableCrop;
    delete raw.cutoutPrompt;
    delete raw.prompt;
    delete raw.cutoutAspectRatio;
    delete raw.aspectRatio;
    delete raw.mode;
  }
  if (
    navId === 'platformListing'
    || navId === 'platformListingJobs'
    || navId === 'platformScheduledListings'
  ) {
    // 上架店铺由自动规则决定：模板不再要求用户选店铺，保存时以首条规则的店铺回填 shopId（兼容后端算子与旧链路）
    raw.autoCreateListingJob = raw.autoCreateListingJob !== false;
    raw.listingTemplateIds = Array.isArray(raw.listingTemplateIds)
      ? raw.listingTemplateIds.map(String).filter(Boolean)
      : [];
    // 「关联自动规则」开关：显式 false 表示不关联（仅入库），此时清空规则与派生店铺
    const ruleSwitchOff = !scheduledListingNode && raw.useListingAutoRule === false;
    if (ruleSwitchOff) {
      delete raw.listingAutoRuleIds;
      delete raw.listingAutoRuleId;
      delete raw.listingAutoRuleRefs;
      delete raw.shopId;
      delete raw.shopName;
    }
    // 自动规则支持多选：listingAutoRuleIds 为准，listingAutoRuleId 保留首条（后端单规则链路兼容）
    const autoRuleIds = (Array.isArray(raw.listingAutoRuleIds) ? raw.listingAutoRuleIds : [])
      .map((id) => String(id ?? '').trim())
      .filter((id, idx, arr) => Boolean(id) && arr.indexOf(id) === idx);
    const autoRuleId = raw.listingAutoRuleId != null ? String(raw.listingAutoRuleId).trim() : '';
    const ruleIds = autoRuleIds.length > 0 ? autoRuleIds : (autoRuleId ? [autoRuleId] : []);
    let refs = [];
    if (ruleIds.length > 0) {
      raw.listingAutoRuleIds = ruleIds;
      raw.listingAutoRuleId = ruleIds[0];
      refs = (Array.isArray(raw.listingAutoRuleRefs) ? raw.listingAutoRuleRefs : [])
        .map((ref) => {
          const id = ref && ref.id != null ? String(ref.id).trim() : '';
          if (!id) return null;
          return {
            id,
            name: String(ref.name || '').trim(),
            shopId: ref.shopId != null ? String(ref.shopId).trim() : '',
            shopName: String(ref.shopName || '').trim(),
            platformCode: String(ref.platformCode || '').trim(),
            listingMode: String(ref.listingMode || '').trim(),
            listingIntervalEnabled: !!ref.listingIntervalEnabled,
            listingIntervalMinutes: Number(ref.listingIntervalMinutes) || 0,
            categoryLabel: String(ref.categoryLabel || '').trim(),
            templateLabel: String(ref.templateLabel || '').trim(),
          };
        })
        .filter((ref) => ref && ruleIds.includes(ref.id));
      raw.listingAutoRuleRefs = refs;
      if (refs.length === 0) delete raw.listingAutoRuleRefs;
    } else {
      delete raw.listingAutoRuleIds;
      delete raw.listingAutoRuleId;
      delete raw.listingAutoRuleRefs;
    }
    // 规则快照里带店铺时以规则为准；否则沿用历史 shopId（旧模板/仅入库场景）
    if (!ruleSwitchOff) {
      const ruleShopId = refs.length > 0 ? String(refs[0].shopId || '').trim() : '';
      const shopId = ruleShopId || (raw.shopId != null ? String(raw.shopId).trim() : '');
      if (shopId) {
        raw.shopId = shopId;
        raw.shopName = ruleShopId
          ? String(refs[0].shopName || '').trim()
          : String(raw.shopName || '').trim();
      } else {
        delete raw.shopId;
        delete raw.shopName;
      }
    }
    if (scheduledListingNode) {
      raw.requireScheduledListing = true;
      raw.autoCreateListingJob = true;
      delete raw.useListingAutoRule;
    } else {
      delete raw.requireScheduledListing;
      if (ruleSwitchOff) raw.useListingAutoRule = false;
      else if (raw.useListingAutoRule === true) raw.useListingAutoRule = true;
      else delete raw.useListingAutoRule;
    }
    const scheduledAt = String(raw.listingScheduledAt || '').trim();
    if (scheduledAt) raw.listingScheduledAt = scheduledAt;
    else delete raw.listingScheduledAt;
  }
  if (navId === 'sourcingHub') {
    // 插件采集节点：可选「采集批次」，选中后直接用该批次图片建任务，无需再上传本地图片。
    const batchKey = String(raw.sourcingBatchKey || '').trim();
    if (batchKey) {
      raw.sourcingBatchKey = batchKey;
      raw.sourcingBatchLabel = String(raw.sourcingBatchLabel || '').trim();
    } else {
      delete raw.sourcingBatchKey;
      delete raw.sourcingBatchLabel;
    }
    const itemIds = Array.isArray(raw.sourcingItemIds)
      ? raw.sourcingItemIds.map((v) => String(v).trim()).filter(Boolean)
      : [];
    if (itemIds.length) raw.sourcingItemIds = itemIds;
    else delete raw.sourcingItemIds;
  }
  if (navId === 'infringementDetection') {
    raw.filterMode = raw.filterMode === 'deep' ? 'deep' : 'basic';
    delete raw.blockOnHighRisk;
  }
  if (navId === 'goods') {
    const studioKeys = Object.keys(GOODS_STUDIO_DEFAULT_PARAMS);
    const next = { composeMode: 'ai' };
    studioKeys.forEach((key) => {
      if (raw[key] !== undefined && raw[key] !== null) next[key] = raw[key];
    });
    next.composeMode = 'ai';
    next.country = String(next.country || GOODS_STUDIO_DEFAULT_PARAMS.country);
    next.countryStyle = String(next.countryStyle || GOODS_STUDIO_DEFAULT_PARAMS.countryStyle);
    next.language = String(next.language || GOODS_STUDIO_DEFAULT_PARAMS.language);
    next.mainImageCount = Math.max(1, Math.min(4, Number(next.mainImageCount) || GOODS_STUDIO_DEFAULT_PARAMS.mainImageCount));
    next.detailPageCount = Math.max(0, Math.min(4, Number(next.detailPageCount) ?? GOODS_STUDIO_DEFAULT_PARAMS.detailPageCount));
    next.mainImageRatio = String(next.mainImageRatio || GOODS_STUDIO_DEFAULT_PARAMS.mainImageRatio);
    next.detailPageRatio = String(next.detailPageRatio || GOODS_STUDIO_DEFAULT_PARAMS.detailPageRatio);
    next.productInfo = String(next.productInfo || '');
    const caseId = next.goodsStudioPromptCaseId != null ? String(next.goodsStudioPromptCaseId).trim() : '';
    if (caseId) next.goodsStudioPromptCaseId = caseId;
    else delete next.goodsStudioPromptCaseId;
    if (next.goodsStudioPromptCaseScope != null && String(next.goodsStudioPromptCaseScope).trim()) {
      next.goodsStudioPromptCaseScope = normalizeTitlePresetScope(next.goodsStudioPromptCaseScope);
    } else {
      delete next.goodsStudioPromptCaseScope;
    }
    delete next.platform;
    delete next.region;
    delete next.styleNote;
    Object.keys(raw).forEach((k) => { delete raw[k]; });
    Object.assign(raw, next);
  }
  if (navId === 'imageFission') {
    delete raw.fissionMode;
    delete raw.referenceStrength;
    delete raw.backgroundColor;
    delete raw.fissionShape;
    delete raw.customAspectWidth;
    delete raw.customAspectHeight;

    raw.patternOnly = raw.patternOnly === true;
    const count = Number(raw.variationCount);
    raw.variationCount = [1, 2, 3, 4].includes(count) ? count : 1;

    const ratio = String(raw.aspectRatio || DEFAULT_FISSION_ASPECT_SIZE.aspectRatio);
    if (ratio === 'original') {
      raw.aspectRatio = 'original';
      raw.outputWidth = 0;
      raw.outputHeight = 0;
    } else {
      raw.aspectRatio = ratio;
      let w = Math.max(0, Math.floor(Number(raw.outputWidth) || 0));
      let h = Math.max(0, Math.floor(Number(raw.outputHeight) || 0));
      const preset = getPresetDimensions(ratio);
      if (preset && (w <= 0 || h <= 0)) {
        w = preset.width;
        h = preset.height;
      }
      if (w <= 0 || h <= 0) {
        w = DEFAULT_FISSION_ASPECT_SIZE.outputWidth;
        h = DEFAULT_FISSION_ASPECT_SIZE.outputHeight;
        raw.aspectRatio = DEFAULT_FISSION_ASPECT_SIZE.aspectRatio;
      }
      raw.outputWidth = w;
      raw.outputHeight = h;
    }
  }
  delete raw.manualReview;
  const rawLabel = String(s.label || '').trim();
  const label = navId === 'goods' && isLegacyGoodsStudioLabel(rawLabel)
    ? (meta?.label || '主图创作')
    : (rewriteLegacyGoodsStudioUserCopy(rawLabel) || meta?.label || navId);
  return {
    navId,
    label,
    onFailure,
    params: { ...defaultParamsForNav(navId), ...raw },
  };
}

function linearStepsToStages(steps) {
  if (!Array.isArray(steps)) return [];
  return steps.map((s) => [normalizeStep(s)]).filter((stage) => stage[0]?.navId);
}

export function normalizeTemplate(t) {
  if (!t || typeof t !== 'object') return null;
  const canvas = t.canvas && typeof t.canvas === 'object'
    ? t.canvas
    : undefined;

  let stepStages;
  if (canvas) {
    stepStages = canvasToStepStages(resolveCanvasFromTemplate({ ...t, canvas }));
  } else if (Array.isArray(t.stepStages) && t.stepStages.length > 0) {
    stepStages = t.stepStages
      .map((stage) => (Array.isArray(stage) ? stage.map(normalizeStep).filter((s) => s?.navId) : []))
      .filter((stage) => stage.length > 0);
  }

  const steps = stepStages?.length
    ? flattenStepStages(stepStages)
    : (Array.isArray(t.steps) ? t.steps.map(normalizeStep) : []);

  if (!stepStages?.length && steps.length > 0) {
    stepStages = linearStepsToStages(steps);
  }

  return {
    ...t,
    name: rewriteLegacyGoodsStudioUserCopy(t.name || '') || t.name,
    group: t.group || '',
    steps,
    stepStages: stepStages || [],
    ...(canvas ? { canvas } : {}),
  };
}

/**
 * 非官方模板是否属于当前用户。
 * 无 ownerId 的旧数据一律不认领，避免换号后串号/误改。
 */
export function isWorkflowTemplateOwnedByUser(template, userId) {
  if (!template || template.official) return false;
  if (!userId || !template.ownerId) return false;
  return String(template.ownerId) === String(userId);
}

function isBoundOwner(userId) {
  const owner = getIpeUserLocalOwnerId();
  return Boolean(owner && userId && String(owner) === String(userId));
}

/** 仅读取当前 bind 用户命名空间下的非官方模板（不读旧全局 key） */
function loadUserOwnedWorkflowTemplates(userId) {
  if (!isBoundOwner(userId)) return [];
  try {
    const parsed = userLocal.getJson(WORKFLOW_TEMPLATES_STORAGE_KEY, null);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    const owner = String(userId);
    const deleted = loadDeletedWorkflowTemplateIds();
    return parsed
      .map((t) => normalizeTemplate(t))
      .filter((t) => t && !t.official && !deleted.has(t.id))
      .map((t) => ({ ...t, ownerId: owner }));
  } catch {
    return [];
  }
}

function persistUserOwnedWorkflowTemplates(list, userId) {
  if (!isBoundOwner(userId)) return;
  const owner = String(userId);
  const toSave = (list || [])
    .map((t) => normalizeTemplate(t))
    .filter((t) => t && !t.official)
    .map((t) => ({ ...t, ownerId: owner }));
  userLocal.setJson(WORKFLOW_TEMPLATES_STORAGE_KEY, toSave);
}

function loadAllWorkflowTemplatesForUser(userId) {
  return loadUserOwnedWorkflowTemplates(userId);
}

/** 登录 bind 时丢弃旧全局 key；退出时同样清理残留全局 key */
export function clearWorkflowTemplatesForLogout() {
  userLocal.dropLegacyKeys(
    WORKFLOW_TEMPLATES_STORAGE_KEY,
    WORKFLOW_CUSTOM_GROUPS_KEY,
    WORKFLOW_TEMPLATES_DELETED_IDS_KEY,
  );
}

/** 加载当前用户可见模板：官方全员可见；本地自定义仅本人；服务端自定义按工作区（含队友） */
export function loadWorkflowTemplates(userId) {
  const all = loadAllWorkflowTemplatesForUser(userId);
  const localVisible = all.filter((t) => t.official || isWorkflowTemplateOwnedByUser(t, userId));
  return mergeLocalAndServerTemplates(localVisible, userId);
}

/**
 * 仅持久化「当前用户自己的」自定义模板到 localStorage。
 * 服务端下发的队友模板不得写入本地，避免串号/误改 owner。
 */
export function saveWorkflowTemplates(visibleList, userId) {
  const deleted = loadDeletedWorkflowTemplateIds();
  const toSave = (visibleList || []).map((t) => {
    const normalized = normalizeTemplate(t);
    if (!normalized || normalized.official || deleted.has(normalized.id)) return null;
    if (userId && normalized.ownerId && !isWorkflowTemplateOwnedByUser(normalized, userId)) {
      return null;
    }
    if (userId) normalized.ownerId = String(userId);
    return normalized;
  }).filter(Boolean);
  persistUserOwnedWorkflowTemplates(toSave, userId);
}

export function getWorkflowTemplateById(id, userId) {
  if (isWorkflowTemplateDeleted(id)) return null;
  const fromServerOfficial = getCachedServerOfficialWorkflowTemplates().find((x) => x.id === id);
  if (fromServerOfficial) return fromServerOfficial;
  const fromServerCustom = getCachedServerCustomWorkflowTemplates().find((x) => x.id === id);
  if (fromServerCustom) return fromServerCustom;
  const t = loadAllWorkflowTemplatesForUser(userId).find((x) => x.id === id) || null;
  if (!t) return null;
  if (t.official || isWorkflowTemplateOwnedByUser(t, userId)) return t;
  return null;
}

export function upsertWorkflowTemplate(template, userId, workspaceContext = null) {
  let normalized = normalizeTemplate(template);
  if (!normalized) return null;
  if (!normalized.official && isWorkflowTemplateDeleted(normalized.id)) return null;
  if (!normalized.official && userId) normalized.ownerId = String(userId);
  if (!normalized.official && workspaceContext?.scope) {
    normalized = applyWorkflowWorkspaceFields(
      normalized,
      workspaceContext.user,
      userId,
      workspaceContext.scope,
    );
  }
  // 官方模板以种子 / 服务端为准，不写入用户本地库
  if (normalized.official) return normalized;

  const owned = loadUserOwnedWorkflowTemplates(userId);
  const idx = owned.findIndex((t) => t.id === normalized.id);
  const existing = idx >= 0 ? owned[idx] : null;
  const now = new Date().toISOString();
  // 编辑页保存时 payload 不含时间戳 / 创建者展示名，需从已有记录合并
  normalized.createdAt = existing?.createdAt || normalized.createdAt || now;
  normalized.updatedAt = now;
  if (!normalized.ownerName && existing?.ownerName) {
    normalized.ownerName = existing.ownerName;
  }
  if (idx >= 0) owned[idx] = normalized;
  else owned.push(normalized);
  persistUserOwnedWorkflowTemplates(owned, userId);
  return normalized;
}

/**
 * 本地 upsert 后同步到服务端（当前工作区）。
 * 失败时仍保留本地结果，由调用方决定是否提示。
 */
export async function syncUpsertWorkflowTemplateToServer(template) {
  const normalized = normalizeTemplate(template);
  if (!normalized?.id || normalized.official || isWorkflowTemplateDeleted(normalized.id)) {
    return { ok: false, template: normalized, skipped: true };
  }
  try {
    const res = await workflowTemplateAPI.upsert(normalized.id, normalized);
    // 上传过程中用户已删除：立刻再删一次，避免迁移把刚删的模板写回服务端
    if (isWorkflowTemplateDeleted(normalized.id)) {
      try {
        await workflowTemplateAPI.delete(normalized.id);
      } catch {
        /* 本地墓碑仍会挡住列表 */
      }
      serverCustomWorkflowCache = serverCustomWorkflowCache.filter((t) => t.id !== normalized.id);
      return { ok: false, template: normalized, skipped: true };
    }
    const saved = normalizeTemplate({ ...(res?.template || normalized), official: false });
    if (saved?.id) {
      const idx = serverCustomWorkflowCache.findIndex((t) => t.id === saved.id);
      if (idx >= 0) serverCustomWorkflowCache[idx] = saved;
      else serverCustomWorkflowCache = [...serverCustomWorkflowCache, saved];
    }
    return { ok: true, template: saved || normalized };
  } catch (error) {
    return { ok: false, template: normalized, error };
  }
}

/** 从本地移除自定义模板（不删官方），并记下墓碑，刷新后不再从服务端/迁移恢复 */
export function removeWorkflowTemplateLocal(templateId, userId) {
  const id = String(templateId || '');
  if (!id) return;
  rememberDeletedWorkflowTemplate(id);
  const owned = loadUserOwnedWorkflowTemplates(userId).filter((t) => t.id !== id);
  persistUserOwnedWorkflowTemplates(owned, userId);
  serverCustomWorkflowCache = serverCustomWorkflowCache.filter((t) => t.id !== id);
}

/** 本地删除后同步删除服务端记录 */
export async function syncDeleteWorkflowTemplateFromServer(templateId) {
  const id = String(templateId || '');
  if (!id) return { ok: false, skipped: true };
  try {
    await workflowTemplateAPI.delete(id);
    serverCustomWorkflowCache = serverCustomWorkflowCache.filter((t) => t.id !== id);
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * 将本地有、服务端尚无的本人模板补传到当前工作区（一次性迁移）。
 * 不覆盖服务端已有同 id 记录。
 */
export async function migrateLocalWorkflowTemplatesToServer(userId) {
  if (!userId) return { uploaded: 0 };
  const localOwned = loadUserOwnedWorkflowTemplates(userId);
  const serverIds = new Set(serverCustomWorkflowCache.map((t) => t.id));
  let uploaded = 0;
  for (const t of localOwned) {
    if (!t?.id || serverIds.has(t.id) || isWorkflowTemplateDeleted(t.id)) continue;
    const result = await syncUpsertWorkflowTemplateToServer(t);
    if (result.ok) {
      uploaded += 1;
      serverIds.add(t.id);
    }
  }
  return { uploaded };
}

/** 加载当前用户自定义分组（仅本人命名空间；不迁入旧全局 map） */
export function loadWorkflowCustomGroups(userId) {
  if (!isBoundOwner(userId)) return [];
  const groups = userLocal.getJson(WORKFLOW_CUSTOM_GROUPS_KEY, []);
  return Array.isArray(groups) ? groups.filter(Boolean) : [];
}

export function saveWorkflowCustomGroups(userId, groups) {
  if (!isBoundOwner(userId)) return;
  userLocal.setJson(WORKFLOW_CUSTOM_GROUPS_KEY, (groups || []).filter(Boolean));
}

/** 批量应用：仅当模板含「AI文案」时可自动创建任务；其前序步骤需人工在工作台完成 */
export function analyzeTemplateForBatchApply(template) {
  const steps = template?.steps || [];
  const copyIdx = steps.findIndex((s) => s.navId === 'aiCopywriting' || s.navId === 'titles');
  if (copyIdx < 0) {
    return { canAuto: false, message: '该模板未包含「AI文案」步骤，无法在此批量自动执行。请在工作台打开对应工具逐步处理。' };
  }
  const prior = steps.slice(0, copyIdx);
  return { canAuto: true, titlesStepIndex: copyIdx, priorManualSteps: prior };
}
